import { useState, useEffect } from 'react'
import AdminSidebar from "../../components/admin/AdminSidebar"
import AdminHeader from "../../components/admin/AdminHeader"
import { useNewsletterCampaigns } from "../../hooks/useNewsletter"
import { Button } from "../../components/ui/button"
import { Plus, Send, Trash2, Eye, Search, Loader2, Mail, Calendar, BarChart3 } from "lucide-react"
import { useTranslation } from "../../i18n"
import NewsletterCampaignModal from "../../components/admin/NewsletterCampaignModal"

export default function NewsletterCampaignsPage() {
  const { tObj } = useTranslation()
  const [statusFilter, setStatusFilter] = useState(null)
  const [page, setPage] = useState(1)
  const [limit] = useState(10)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingCampaign, setEditingCampaign] = useState(null)

  const { campaigns, pagination, loading, fetchCampaigns, sendCampaign, deleteCampaign } = useNewsletterCampaigns({
    page,
    limit,
    status: statusFilter,
    immediate: true
  })

  useEffect(() => {
    fetchCampaigns()
  }, [page, statusFilter])

  const handleCreate = () => {
    setEditingCampaign(null)
    setIsModalOpen(true)
  }

  const handleSend = async (campaignId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir envoyer cette campagne à tous les abonnés actifs ?')) {
      return
    }
    try {
      await sendCampaign(campaignId)
    } catch (err) {
      // Erreur déjà gérée par le hook
    }
  }

  const handleDelete = async (campaignId, subject) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer la campagne "${subject}" ?`)) {
      return
    }
    try {
      await deleteCampaign(campaignId)
    } catch (err) {
      // Erreur déjà gérée par le hook
    }
  }

  const getStatusBadge = (status) => {
    const badges = {
      DRAFT: { text: 'Brouillon', className: 'bg-gray-100 text-gray-800' },
      SCHEDULED: { text: 'Programmée', className: 'bg-blue-100 text-blue-800' },
      SENDING: { text: 'En cours d\'envoi', className: 'bg-yellow-100 text-yellow-800' },
      SENT: { text: 'Envoyée', className: 'bg-green-100 text-green-800' },
      CANCELLED: { text: 'Annulée', className: 'bg-red-100 text-red-800' }
    }
    return badges[status] || badges.DRAFT
  }

  const calculateOpenRate = (campaign) => {
    if (!campaign.sentCount || campaign.sentCount === 0) return 0
    return ((campaign.openedCount / campaign.sentCount) * 100).toFixed(1)
  }

  const calculateClickRate = (campaign) => {
    if (!campaign.sentCount || campaign.sentCount === 0) return 0
    return ((campaign.clickedCount / campaign.sentCount) * 100).toFixed(1)
  }

  if (loading && !campaigns.length) {
    return (
      <div className="flex h-screen bg-gray-50">
        <AdminSidebar />
        <div className="flex-1 ml-64 p-8 flex items-center justify-center">
          <Loader2 className="w-10 h-10 animate-spin text-[#141835]" />
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-screen bg-gray-50">
      <AdminSidebar />
      <div className="flex-1 ml-64 p-8 overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <AdminHeader title="Gestion des campagnes de newsletter" />
          <Button onClick={handleCreate} className="bg-[#141835] hover:bg-[#1a1f4a] text-white">
            <Plus className="w-4 h-4 mr-2" />
            Créer une campagne
          </Button>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex flex-wrap gap-2">
            <Button
              variant={statusFilter === null ? "default" : "outline"}
              onClick={() => setStatusFilter(null)}
              className={statusFilter === null ? "bg-[#141835] text-white" : ""}
            >
              Toutes
            </Button>
            <Button
              variant={statusFilter === 'DRAFT' ? "default" : "outline"}
              onClick={() => setStatusFilter('DRAFT')}
              className={statusFilter === 'DRAFT' ? "bg-[#141835] text-white" : ""}
            >
              Brouillons
            </Button>
            <Button
              variant={statusFilter === 'SCHEDULED' ? "default" : "outline"}
              onClick={() => setStatusFilter('SCHEDULED')}
              className={statusFilter === 'SCHEDULED' ? "bg-[#141835] text-white" : ""}
            >
              Programmées
            </Button>
            <Button
              variant={statusFilter === 'SENT' ? "default" : "outline"}
              onClick={() => setStatusFilter('SENT')}
              className={statusFilter === 'SENT' ? "bg-[#141835] text-white" : ""}
            >
              Envoyées
            </Button>
          </div>
        </div>

        {/* Campaigns List */}
        <div className="space-y-4">
          {campaigns.length > 0 ? (
            campaigns.map((campaign) => {
              const statusBadge = getStatusBadge(campaign.status)
              return (
                <div key={campaign.id} className="bg-white rounded-lg shadow-sm p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-semibold text-gray-900">{campaign.subject}</h3>
                        <span className={`px-2 py-1 text-xs rounded-full ${statusBadge.className}`}>
                          {statusBadge.text}
                        </span>
                      </div>
                      
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                        <div>
                          <p className="text-xs text-gray-500">Destinataires</p>
                          <p className="text-sm font-medium">{campaign.totalRecipients || 0}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Envoyés</p>
                          <p className="text-sm font-medium">{campaign.sentCount || 0}</p>
                        </div>
                        {campaign.status === 'SENT' && (
                          <>
                            <div>
                              <p className="text-xs text-gray-500">Taux d'ouverture</p>
                              <p className="text-sm font-medium text-blue-600">{calculateOpenRate(campaign)}%</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500">Taux de clic</p>
                              <p className="text-sm font-medium text-green-600">{calculateClickRate(campaign)}%</p>
                            </div>
                          </>
                        )}
                      </div>

                      {campaign.scheduledAt && (
                        <div className="mt-3 flex items-center gap-2 text-sm text-gray-600">
                          <Calendar size={16} />
                          <span>
                            Programmée pour le {new Date(campaign.scheduledAt).toLocaleDateString('fr-FR', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </span>
                        </div>
                      )}

                      {campaign.sentAt && (
                        <div className="mt-2 flex items-center gap-2 text-sm text-gray-600">
                          <Mail size={16} />
                          <span>
                            Envoyée le {new Date(campaign.sentAt).toLocaleDateString('fr-FR', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </span>
                        </div>
                      )}
                    </div>

                    <div className="flex gap-2 ml-4">
                      {campaign.status === 'DRAFT' && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleSend(campaign.id)}
                        >
                          <Send className="w-4 h-4 mr-2" />
                          Envoyer
                        </Button>
                      )}
                      {campaign.status !== 'SENDING' && campaign.status !== 'SENT' && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDelete(campaign.id, campaign.subject)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              )
            })
          ) : (
            <div className="bg-white rounded-lg shadow-sm p-12 text-center">
              <Mail className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Aucune campagne</h3>
              <p className="text-gray-600 mb-4">Créez votre première campagne de newsletter</p>
              <Button onClick={handleCreate} className="bg-[#141835] hover:bg-[#1a1f4a] text-white">
                <Plus className="w-4 h-4 mr-2" />
                Créer une campagne
              </Button>
            </div>
          )}
        </div>

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="mt-6 bg-white rounded-lg shadow-sm px-6 py-4 flex items-center justify-between">
            <div className="text-sm text-gray-700">
              Page {pagination.page} sur {pagination.totalPages} ({pagination.total} campagnes)
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={pagination.page === 1}
              >
                Précédent
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.min(pagination.totalPages, p + 1))}
                disabled={pagination.page === pagination.totalPages}
              >
                Suivant
              </Button>
            </div>
          </div>
        )}

        <NewsletterCampaignModal
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false)
            setEditingCampaign(null)
            fetchCampaigns()
          }}
          campaign={editingCampaign}
        />
      </div>
    </div>
  )
}
